import React from "react";
import { Popover, TextField, MenuItem, Select, FormControl, InputLabel, TextareaAutosize, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PopoverContent } from "../workflowBuilder/style";
import {
    ToolBarContainer,
    ToolConfigurationHeader,
    ToolConfigurationBody,
    ToolConfigurationFooter,
    ToolConfigurationCancelButton,
    ToolConfigurationSaveButton,
} from "../workflowBuilder/style";
import InfoIcon from "../../assets/workflows/info-icon.svg";
import { useRef } from "react";
import ComputerIcon from "../../assets/computer-icon.svg";
import AvatarDriveFilePicker from "./AvatarDriveFilePicker";
import PdfIcon from "../../assets/pdf-large.svg";
import DocxIcon from "../../assets/docx-large.svg";
import TxtIcon from "../../assets/txt-large.svg";
import closeX from "../../assets/x-close.svg";
import { UploadBtn, FileTypeTxt, FilesDiv, FileCard } from "./style";

const SkillConfigPopover = ({
    anchorEl,
    onClose,
    onSave,
    skill,
    configValues,
    setConfigValues,
    onFileUpload,
    onRemoveFile,
    uploadedFiles,
    handleDriveFiles,
    showError: parentShowError,
    isDriveFilesLoading,
}) => {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const [configErrors, setConfigErrors] = React.useState({});
    const [showFileError, setShowFileError] = React.useState(false);

    if (!skill) return null;

    let configuration = [];
    try {
        configuration = typeof skill.configuration === "string"
            ? JSON.parse(skill.configuration)
            : (skill.configuration || []);
    } catch (error) {
        console.error("Error parsing configuration:", error);
    }

    const handleInputChange = (title, value) => {
        setConfigValues((prev) => ({
            ...prev,
            [title]: value,
        }));
        // Clear error when user types
        if (configErrors[title]) {
            setConfigErrors(prev => ({ ...prev, [title]: false }));
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e) => {
        if (e.target.files) {
            onFileUpload(e.target.files);
            setShowFileError(false);
        }
    };

    const getFileIcon = (fileType) => {
        if (fileType.includes("pdf")) return PdfIcon;
        if (fileType.includes("docx") || fileType.includes("doc")) return DocxIcon;
        return TxtIcon;
    };

    const validate = () => {
        const errors = {};
        configuration.forEach(config => {
            if (config.required && !configValues[config.title]?.trim()) {
                errors[config.title] = true;
            }
        });
        setConfigErrors(errors);

        const fileError = skill.isRequiredDocument && uploadedFiles.length === 0;
        setShowFileError(fileError);

        return Object.keys(errors).length === 0 && !fileError;
    };

    const handleRun = () => {
        if (validate()) {
            // Construct the prompt with configuration values
            let message = skill.isSkill !== false ? `Run the skill ${skill.toolName}` : skill.toolTrigger;
            const configDetails = Object.entries(configValues)
                .filter(([_, value]) => value)
                .map(([title, value]) => `- ${title}: ${value}`)
                .join("\n\n");

            if (configDetails) {
                message += `\n\nConfiguration:\n\n${configDetails}`;
            }

            onSave(message, skill, uploadedFiles);
        }
    };

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
            transformOrigin={{
                vertical: "bottom",
                horizontal: "left",
            }}
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: "1.25rem",
                        boxShadow: "0px 20px 50px rgba(0, 0, 0, 0.15)",
                        width: "420px",
                        maxHeight: "85vh",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        backdropFilter: "blur(10px)",
                    },
                },
            }}
        >
            <PopoverContent style={{ width: "100%", display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
                <ToolBarContainer style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
                    <ToolConfigurationHeader style={{ padding: "1.5rem 1.5rem 1rem 1.5rem", flexShrink: 0 }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                            <div style={{ fontSize: "1.1rem", fontWeight: "700", letterSpacing: "-0.02em" }}>
                                {skill.toolName}
                            </div>
                            <div style={{ fontSize: "0.8rem", fontWeight: "400", opacity: 0.9 }}>
                                {skill.toolDescription}
                            </div>
                        </div>
                    </ToolConfigurationHeader>

                    <ToolConfigurationBody style={{ 
                        gap: "1.5rem", 
                        padding: "0 1.5rem 1.5rem 1.5rem", 
                        backgroundColor: "#fdfdfd",
                        flex: 1,
                        overflowY: "auto",
                        overflowX: "hidden"
                    }}>
                        {configuration.map((config, index) => (
                            <div key={index} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                <div style={{ fontSize: "0.9rem", fontWeight: "600", color: "#333" }}>
                                    {config.title} {config.required && <span style={{ color: "#ef4444" }}>*</span>}
                                </div>

                                {config.type === "Text Field" ? (
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder={config.hint}
                                        value={configValues[config.title] || ""}
                                        onChange={(e) => handleInputChange(config.title, e.target.value)}
                                        required={config.required}
                                        error={configErrors[config.title]}
                                        helperText={configErrors[config.title] ? t("common.required") : ""}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '0.75rem',
                                                backgroundColor: '#fff',
                                            }
                                        }}
                                    />
                                ) : config.type === "Text Area" ? (
                                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                        <TextareaAutosize
                                            minRows={3}
                                            placeholder={config.hint}
                                            value={configValues[config.title] || ""}
                                            onChange={(e) => handleInputChange(config.title, e.target.value)}
                                            style={{
                                                width: "100%",
                                                padding: "12px",
                                                margin: 0,
                                                borderRadius: "0.75rem",
                                                border: configErrors[config.title] ? "1px solid #d32f2f" : "1px solid rgba(0, 0, 0, 0.23)",
                                                fontFamily: "inherit",
                                                fontSize: "14px",
                                                outline: "none",
                                                transition: "border-color 0.2s",
                                                backgroundColor: "#fff",
                                                boxSizing: "border-box",
                                                resize: "vertical"
                                            }}
                                        />
                                        {configErrors[config.title] && (
                                            <span style={{ color: "#d32f2f", fontSize: "0.75rem", marginLeft: "14px" }}>
                                                {t("common.required")}
                                            </span>
                                        )}
                                    </div>
                                ) : config.type === "Dropdown" ? (
                                    <FormControl fullWidth size="small" error={configErrors[config.title]}>
                                        <InputLabel id={`label-${index}`}>{config.title}</InputLabel>
                                        <Select
                                            labelId={`label-${index}`}
                                            value={configValues[config.title] || ""}
                                            label={config.title}
                                            onChange={(e) => handleInputChange(config.title, e.target.value)}
                                            sx={{
                                                borderRadius: '0.75rem',
                                                backgroundColor: '#fff',
                                            }}
                                        >
                                            {config.options?.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {configErrors[config.title] && (
                                            <span style={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "4px" }}>
                                                {t("common.required")}
                                            </span>
                                        )}
                                    </FormControl>
                                ) : null}

                                <div style={{ fontSize: "0.75rem", color: "#6b7280", display: "flex", alignItems: "center", gap: "6px" }}>
                                    <img src={InfoIcon} alt="info" style={{ width: "14px", opacity: 0.7 }} />
                                    {config.description}
                                </div>
                            </div>
                        ))}

                        {skill.isRequiredDocument && (
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", alignItems: "center", paddingTop: "0.5rem" }}>
                                <div style={{ fontSize: "0.9rem", fontWeight: "600", color: "#333", alignSelf: "flex-start" }}>
                                    {t("avatarChat.uploadFiles")} <span style={{ color: "#ef4444" }}>*</span>
                                </div>
                                {isDriveFilesLoading ? (
                                    <CircularProgress size={24} />
                                ) : (
                                    <>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            style={{ display: "none" }}
                                            onChange={handleFileChange}
                                            accept=".pdf,.doc,.docx,.txt"
                                            multiple
                                        />

                                        <UploadBtn onClick={handleUploadClick} style={{ width: "100%", borderRadius: "0.75rem" }}>
                                            <img src={ComputerIcon} alt="computer" />
                                            {t("avatarChat.uploadFromComputer")}
                                        </UploadBtn>

                                        <AvatarDriveFilePicker
                                            isInPopover={true}
                                            onFilesSelected={handleDriveFiles}
                                        />
                                    </>
                                )}

                                <FileTypeTxt>{t("avatarChat.fileTypes")}</FileTypeTxt>

                                {showFileError && uploadedFiles.length === 0 && (
                                    <div style={{ color: "#ef4444", fontSize: "12px", alignSelf: "flex-start" }}>
                                        {t("avatarChat.uploadAtLeastOneFile")}
                                    </div>
                                )}

                                {uploadedFiles.length > 0 && (
                                    <FilesDiv style={{ width: "100%", maxHeight: "200px" }}>
                                        <div style={{ fontSize: "0.8rem", fontWeight: "600", marginBottom: "0.5rem" }}>
                                            {t("avatarChat.files")} ({uploadedFiles.length})
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                                            {uploadedFiles.map((file, index) => (
                                                <FileCard key={`${file.name}-${index}`} style={{ borderRadius: "0.5rem" }}>
                                                    <img src={getFileIcon(file.type)} alt={file.type} style={{ width: "18px" }} />
                                                    <span style={{ fontSize: "13px", color: "#444", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                        {file.name}
                                                    </span>
                                                    <img
                                                        src={closeX}
                                                        alt="remove"
                                                        style={{ marginLeft: "auto", cursor: "pointer", width: "14px" }}
                                                        onClick={() => onRemoveFile(file.name)}
                                                    />
                                                </FileCard>
                                            ))}
                                        </div>
                                    </FilesDiv>
                                )}
                            </div>
                        )}
                    </ToolConfigurationBody>

                    <ToolConfigurationFooter style={{ 
                        padding: "1rem 1.5rem 1.5rem 1.5rem", 
                        backgroundColor: "#fff", 
                        borderTop: "1px solid #f3f4f6", 
                        justifyContent: "flex-end", 
                        gap: "10px",
                        flexShrink: 0
                    }}>
                        <ToolConfigurationCancelButton
                            variant="outlined"
                            onClick={onClose}
                            style={{ borderRadius: "0.75rem", textTransform: "none", fontWeight: 600, height: "40px" }}
                        >
                            {t("avatarChat.cancel")}
                        </ToolConfigurationCancelButton>
                        <ToolConfigurationSaveButton
                            variant="contained"
                            onClick={handleRun}
                            style={{ borderRadius: "0.75rem", textTransform: "none", fontWeight: 600, paddingLeft: "2rem", paddingRight: "2rem", height: "40px" }}
                        >
                            {t("avatarChat.run")}
                        </ToolConfigurationSaveButton>
                    </ToolConfigurationFooter>
                </ToolBarContainer>
            </PopoverContent>
        </Popover>
    );
};

export default SkillConfigPopover;
